import type { Dispatch } from 'react';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { NotificationType } from '../../feature-service-types';
import type { NotificationDisplayService } from '../../feature-service-types';
import {
  DEALER_CONNECTED_NOTIFICATION,
  DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE,
} from '../../utils/globals';
import { PoiIcon } from '../../components/Icons/PoiIcon';
import { TextButton, TextLink } from '@oneaudi/unified-web-components';
import {
  SYS_COLOR_CANVAS_100_DARK,
  SYS_COLOR_CANVAS_100_LIGHT,
} from '@oneaudi/unified-web-alpha-common';
import type {
  ConnectedData,
  Dealer,
  geoLocationOption,
} from '../../interfaces/header-components.interfaces';
import { ConfigContext } from '../../context/ConfigContext';
import { messages } from '../../i18n/messages';
import { useI18n } from '@oneaudi/i18n-context';
import { ServiceContext } from '../../context/ServiceContext';
import { createSetActiveItemAction, type IHeaderAction } from '../../actions/header-actions';
import { removePartnerID } from '../../utils/append-partner-id';
import { parseCompositeDealerId } from '../../utils/dealer-id-parser';
import { saveDealerTrackingData } from '../../utils/tracking';

const DEALER_SEARCH_NOTIFICATION = 'dealer-search-notfication';

interface UseDealerNotificationProps {
  isTierOneDomain: boolean;
  dealerData: Dealer | undefined;
  headerDispatch: Dispatch<IHeaderAction>;
  setDealerConnectedNotification: Dispatch<React.SetStateAction<ConnectedData | null>>;
  onDealerConfirmed?: () => void;
  hasDealerSearchParam?: boolean;
}

interface DealerNotificationObject {
  dealerConnected: boolean;
  geoLocationSuggestion: geoLocationOption;
}

interface SuggestDealerObject {
  notification: string;
  confirm: string;
  choose: string;
}

/**
 * Create a dealer connection notification when a dealer is connected.
 * Only renders the notification on TierOne domains when dealer data is available.
 *
 * @param {NotificationDisplayService} notificationDisplayService - Notification display service
 * @param {string} dealerConnectedNofication - The text to display in the notification
 * @param {Dealer | undefined} dealerData - Dealer data
 * @param {Dispatch<React.SetStateAction<ConnectedData | null>>} setDealerConnectedNotification - Set dealer connected notification state
 * @param {geoLocationOption} geoLocationOption - Geo location option
 */
function createAutoConnectNotification(
  notificationDisplayService: NotificationDisplayService,
  dealerConnectedNofication: string,
  dealerData: Dealer | undefined,
  setDealerConnectedNotification: Dispatch<React.SetStateAction<ConnectedData | null>>,
  geoLocationOption: geoLocationOption,
) {
  const isOpen: boolean = notificationDisplayService.isOpen(DEALER_SEARCH_NOTIFICATION);

  if (dealerData && !isOpen) {
    notificationDisplayService.createNotification(
      NotificationType.POPUP_GENERIC,
      DEALER_SEARCH_NOTIFICATION,
      dealerConnectedNofication,
      {
        title: dealerData?.displayName || dealerData?.name,
        userCloseable: false,
        icon: (
          <PoiIcon
            color={process.env.IS_LEGACY ? SYS_COLOR_CANVAS_100_DARK : SYS_COLOR_CANVAS_100_LIGHT}
            active
          />
        ),
        autoCloseAfter: 5000,
        onClose: () => {
          sessionStorage?.setItem(DEALER_CONNECTED_NOTIFICATION, 'true');
        },
      },
    );
    const connectedData: ConnectedData = {
      isConnected: true,
      geoLocationOption,
      triggerDealerSearch: false,
      dealerSearchBenefitSelected: false,
    };
    setDealerConnectedNotification(connectedData);
  }
}

function createSuggestDealerNotification(
  notificationDisplayService: NotificationDisplayService,
  suggestDealerObject: SuggestDealerObject,
  dealerData: Dealer | undefined,
  handleDealerConfirm: () => void,
  handleChooseDealer: () => void,
  setSuggestedDealer: Dispatch<React.SetStateAction<boolean>>,
) {
  const isOpen: boolean = notificationDisplayService.isOpen(DEALER_SEARCH_NOTIFICATION);

  if (dealerData && !isOpen) {
    notificationDisplayService.createNotification(
      NotificationType.POPUP_GENERIC,
      DEALER_SEARCH_NOTIFICATION,
      suggestDealerObject.notification,
      {
        title: dealerData?.displayName || dealerData?.name,
        userCloseable: true,
        icon: (
          <PoiIcon
            color={process.env.IS_LEGACY ? SYS_COLOR_CANVAS_100_DARK : SYS_COLOR_CANVAS_100_LIGHT}
            active
          />
        ),
        secondaryButton: (
          <TextButton
            aria-label={suggestDealerObject.confirm}
            data-testid="connect-to-dealer"
            onClick={() => {
              handleDealerConfirm();
            }}
            size="sm"
            title={suggestDealerObject.confirm}
            type="button"
            variant="primary"
            margin={['var(--spacing-relative-md)', 0, 0, 0]}
          >
            {suggestDealerObject.confirm}
          </TextButton>
        ),
        textButton: (
          <TextLink
            aria-label={suggestDealerObject.choose}
            data-testid="different-dealer-link"
            id="different-dealer-link"
            margin={[0, 0, 0, 0]}
            onClick={() => {
              handleChooseDealer();
            }}
            size="sm"
            title={suggestDealerObject.choose}
            variant="standalone"
          >
            {suggestDealerObject.choose}
          </TextLink>
        ),
        onClose: () => {
          sessionStorage?.setItem(DEALER_CONNECTED_NOTIFICATION, 'true');
          setSuggestedDealer(true);
        },
      },
    );
  }
}

/**
 * Custom hook to create a dealer connection notification when a dealer is connected.
 * Only renders the notification on TierOne domains when dealer data is available.
 *
 * @param {boolean} isTierOneDomain - Flag indicating if current domain is TierOne
 * @param {Dealer | undefined} dealerData - Dealer data
 * @param {Dispatch<IHeaderAction>} headerDispatch - Dispatch function for header actions
 * @param {React.Dispatch<React.SetStateAction<ConnectedData | null>>} setDealerConnectedNotification - Set dealer connected notification state
 */
export const useDealerNotification = ({
  isTierOneDomain,
  dealerData,
  headerDispatch,
  setDealerConnectedNotification,
  onDealerConfirmed,
  hasDealerSearchParam,
}: UseDealerNotificationProps) => {
  const { notificationDisplayService, headerStateService, dealerContextService, trackingService } =
    useContext(ServiceContext);
  const { renderDealerContext, geoLocationOption, dealerSearchGeolocationEnabled } =
    useContext(ConfigContext);
  const locationOption: DealerNotificationObject['geoLocationSuggestion'] = geoLocationOption;
  const autoconnectNofication = useI18n(messages.dealership_connected_notification);
  const suggestDealerNotification = useI18n(messages.suggest_dealer_notification);
  const suggestDealerConfirm = useI18n(messages.suggest_dealer_confirm_button);
  const suggestDealerChoose = useI18n(messages.suggest_dealer_choose_button);
  const [confirmedDealer, setConfirmedDealer] = useState<boolean>(false);
  const [suggestedDealer, setSuggestedDealer] = useState<boolean>(false);

  const suggestDealerObject: SuggestDealerObject = useMemo(
    () => ({
      notification: suggestDealerNotification,
      confirm: suggestDealerConfirm,
      choose: suggestDealerChoose,
    }),
    [suggestDealerNotification, suggestDealerConfirm, suggestDealerChoose],
  );

  const handleDealerConfirm = useCallback(() => {
    const connectedData: ConnectedData = {
      isConnected: true,
      geoLocationOption,
      triggerDealerSearch: false,
      dealerSearchBenefitSelected: false,
    };
    setDealerConnectedNotification(connectedData);

    // OptIn: re-set dealer in shared service so other FAs can now see it
    if (geoLocationOption === 'optIn' && dealerData?.dealerId) {
      onDealerConfirmed?.();
      const parsed = parseCompositeDealerId(String(dealerData.dealerId));
      // Cast brand to BrandIdentifier — parseCompositeDealerId validates it's a single uppercase letter
      dealerContextService.setSelectedDealer(parsed as typeof parsed & { brand: 'A' });
      // Broadcast to other FAs
      window.dispatchEvent(new CustomEvent('dealerDataUpdated', { detail: dealerData }));
    }

    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: false,
        showDealer: false,
        showDealerSearch: false,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
    setConfirmedDealer(true);
    trackingService?.track(saveDealerTrackingData(suggestDealerConfirm));
    // close notification
    notificationDisplayService.closeNotification(DEALER_SEARCH_NOTIFICATION);
  }, [
    geoLocationOption,
    dealerData,
    dealerContextService,
    setDealerConnectedNotification,
    headerDispatch,
    headerStateService,
    notificationDisplayService,
    onDealerConfirmed,
    trackingService,
    suggestDealerConfirm,
  ]);

  const handleChooseDealer = useCallback(() => {
    const connectedData: ConnectedData = {
      isConnected: false,
      geoLocationOption,
      triggerDealerSearch: true,
      dealerSearchBenefitSelected: false,
    };
    setDealerConnectedNotification(connectedData);
    headerDispatch(
      createSetActiveItemAction({
        anchor: null,
        headerStateService,
        index: -1,
        showSearch: false,
        showDealer: false,
        showDealerSearch: true,
        showLoginFlyout: false,
        showWistListFlyout: false,
        openDealerDetails: false,
      }),
    );
    setSuggestedDealer(true);
    // close notification
    notificationDisplayService.closeNotification(DEALER_SEARCH_NOTIFICATION);
  }, [
    geoLocationOption,
    setDealerConnectedNotification,
    headerDispatch,
    headerStateService,
    notificationDisplayService,
  ]);

  useEffect(() => {
    const notificationConnected = sessionStorage?.getItem(DEALER_CONNECTED_NOTIFICATION);
    // only create notification once when connected to dealer
    // eslint-disable-next-line no-extra-boolean-cast
    if (!notificationConnected && !Boolean(notificationConnected)) {
      // render notification only if on TierOne domain and dealer data is available, and geoLocationOption is 'optOut'
      if (renderDealerContext && isTierOneDomain && locationOption === 'optOut') {
        createAutoConnectNotification(
          notificationDisplayService,
          autoconnectNofication,
          dealerData,
          setDealerConnectedNotification,
          geoLocationOption,
        );
      }
      // render notification only if on TierOne domain and dealer data is available, and geoLocationOption is 'optIn'
      // Skip optIn notification when URL has dealer-id or consumerLocations params (dealer set immediately)
      if (
        renderDealerContext &&
        isTierOneDomain &&
        locationOption === 'optIn' &&
        !hasDealerSearchParam
      ) {
        createSuggestDealerNotification(
          notificationDisplayService,
          suggestDealerObject,
          dealerData,
          handleDealerConfirm,
          handleChooseDealer,
          setSuggestedDealer,
        );

        // set initial state
        if (dealerData) {
          const connectedData: ConnectedData = {
            isConnected: false,
            geoLocationOption,
            triggerDealerSearch: false,
            dealerSearchBenefitSelected: false,
          };
          setDealerConnectedNotification(connectedData);
        }
      }
    }
  }, [
    isTierOneDomain,
    dealerData,
    renderDealerContext,
    locationOption,
    notificationDisplayService,
    autoconnectNofication,
    geoLocationOption,
    suggestDealerObject,
    handleDealerConfirm,
    handleChooseDealer,
    hasDealerSearchParam,
  ]);

  /**
   * When dealer is suggested but not confirmed (user closed notification or chose different dealer),
   * clear the selected dealer from the dealer context service.
   */
  useEffect(() => {
    if (!confirmedDealer && suggestedDealer) {
      dealerContextService.setSelectedDealer(undefined);
      removePartnerID();
      // unsubscribe from geolocation
      if (dealerSearchGeolocationEnabled) {
        sessionStorage?.setItem(DEALER_CONTEXT_AUTOCONNECT_UNSUBSCRIBE, 'true');
        window.dispatchEvent(new CustomEvent('autoConnectUnsubscribed'));
      }
    }
  }, [confirmedDealer, suggestedDealer, dealerSearchGeolocationEnabled]);
};
